import {
  AssessmentOutcome,
  liveAssessmentOutcomes,
} from "@/constants/terminal/assessment-outcome"
import { CompanyRunOutcome } from "@/constants/terminal/company-run-outcome"
import { cn } from "@/lib/utils"

// A Company Run's path through the runtime, in execution order. Extraction runs
// beside Research, one Source Record Extraction per finished Source Check.
const stages = [
  "Identity",
  "Research",
  "Extraction",
  "Review labels",
  "Assessment",
  "File",
] as const
type StageState = "done" | "active" | "failed" | "stopped" | "pending"

const classNameByStageState: Record<StageState, string> = {
  done: "bg-good-foreground",
  active: "bg-terminal-accent motion-safe:animate-pulse",
  failed: "bg-bad-foreground",
  stopped: "bg-caution-foreground",
  pending: "bg-border-faint",
}

// The furthest stage a Company Run has reached, read from facts it already
// records: admission, its first Source Check, frozen Research Sufficiency, its
// Assessment, and a completed Assessment, whose parts are the File. A
// finished run that never reached its File stopped at that stage.
const companyRunStages = (run: {
  admittedAt: string | null
  outcome: CompanyRunOutcome | null
  hasSourceCheck: boolean
  isResearchFrozen: boolean
  pendingExtractionCount: number
  assessmentId: string | null
  assessmentOutcome: AssessmentOutcome | null
}) => {
  const isFileDone = liveAssessmentOutcomes.some(
    (outcome) => outcome === run.assessmentOutcome
  )
  // Each fact proves the run reached the stage at its index; Extraction has
  // no fact of its own and is reached with Research.
  const reachedFacts = [
    run.admittedAt !== null,
    run.hasSourceCheck,
    false,
    run.isResearchFrozen,
    run.assessmentId !== null,
  ]
  // An assessment-only Run stops after its Assessment, before the File.
  const isAssessed = run.assessmentOutcome === AssessmentOutcome.ASSESSED
  let reached = reachedFacts.lastIndexOf(true)
  if (isFileDone) reached = stages.length
  else if (isAssessed) reached = reachedFacts.length
  const isFailed =
    run.outcome !== null || run.assessmentOutcome === AssessmentOutcome.FAILED
  let reachedState: StageState = isFailed ? "failed" : "active"
  if (run.outcome === CompanyRunOutcome.DEFERRED || isAssessed)
    reachedState = "stopped"
  return stages.map((label, index): { label: string; state: StageState } => {
    if (index < reached) return { label, state: "done" }
    // Extraction follows each Source Check while Research is still open.
    if (label === "Extraction" && reached === 1)
      return {
        label,
        state:
          run.pendingExtractionCount > 0 && reachedState === "active"
            ? "active"
            : "pending",
      }
    return { label, state: index === reached ? reachedState : "pending" }
  })
}

type CompanyRunStageFacts = Parameters<typeof companyRunStages>[0]

export const CompanyRunStageStrip = ({
  run,
  isCompact = false,
}: {
  run: CompanyRunStageFacts
  isCompact?: boolean
}) => {
  const runStages = companyRunStages(run)
  const current = runStages.find(({ state }) => state !== "done")
  return (
    <ol
      aria-label={`Stage: ${current ? `${current.label} ${current.state}` : "File done"}`}
      className={cn(
        "flex",
        isCompact ? "w-24 gap-px" : "max-w-2xl min-w-72 flex-1 gap-1"
      )}
    >
      {runStages.map(({ label, state }) => (
        <li
          key={label}
          title={`${label}: ${state}`}
          className={cn("min-w-0 flex-1", !isCompact && "space-y-1.5")}
        >
          <span
            className={cn(
              "block h-1.5 rounded-xs",
              classNameByStageState[state]
            )}
          />
          {!isCompact && (
            <span
              className={cn(
                "block truncate text-xs",
                state === "pending" && "text-muted-foreground",
                state === "failed" && "text-assessment-critical"
              )}
            >
              {label}
            </span>
          )}
        </li>
      ))}
    </ol>
  )
}
